// 按 manifestSchema.fields 动态生成表单：string/number/date/enum/text/boolean，必填标记、hint、格式预校验
import type { ManifestField, ManifestSchema } from "@csp/shared";
import type { FieldValue, FormValues } from "../lib/manifest";

interface Props {
  schema: ManifestSchema;
  values: FormValues;
  errors: Record<string, string>;
  /** 只在用户碰过或点过提交后显示错误 */
  showErrors: boolean | Record<string, boolean>;
  onChange: (key: string, v: FieldValue) => void;
  onBlur?: (key: string) => void;
}

export function ManifestForm({ schema, values, errors, showErrors, onChange, onBlur }: Props) {
  if (!schema.fields.length) return <p className="muted">这个任务类型不需要填写额外字段。</p>;
  return (
    <div className="form-grid">
      {schema.fields.map((f) => {
        const show = typeof showErrors === "boolean" ? showErrors : !!showErrors[f.key];
        const err = show ? errors[f.key] : undefined;
        return (
          <FieldInput key={f.key} field={f} value={values[f.key]} error={err} onChange={(v) => onChange(f.key, v)} onBlur={() => onBlur?.(f.key)} />
        );
      })}
    </div>
  );
}

function FieldInput({
  field: f,
  value,
  error,
  onChange,
  onBlur,
}: {
  field: ManifestField;
  value: FieldValue | undefined;
  error?: string;
  onChange: (v: FieldValue) => void;
  onBlur: () => void;
}) {
  const id = `mf-${f.key}`;
  const cls = error ? "invalid" : "";
  const s = typeof value === "string" ? value : "";
  let input;
  switch (f.type) {
    case "boolean":
      return (
        <div className="field" style={{ marginBottom: 14, gridColumn: "1 / -1" }}>
          <label className="check" htmlFor={id}>
            <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
            <b>{f.label}</b>
          </label>
          {f.hint && <span className="hint">{f.hint}</span>}
        </div>
      );
    case "enum":
      input = (
        <select id={id} value={s} className={cls} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
          <option value="">请选择…</option>
          {(f.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
      break;
    case "text":
      input = <textarea id={id} value={s} className={cls} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case "date":
      input = <input id={id} type="date" value={s} className={cls} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case "number":
      input = <input id={id} type="number" value={s} className={cls} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    default:
      input = <input id={id} type="text" value={s} className={cls} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />;
  }
  return (
    <label className="field" htmlFor={id} style={f.type === "text" ? { gridColumn: "1 / -1" } : undefined}>
      <span className="lbl">
        {f.label}
        {f.required && <span className="req" title="必填">*</span>}
      </span>
      {input}
      {f.hint && !error && <span className="hint">{f.hint}</span>}
      {error && <span className="field-err">{error}</span>}
    </label>
  );
}
